import { siteConfig } from "@/config/site";

import { getRuntimeAPIBaseURL, isPreview } from "@/lib/runtime-env";

type Env = "dev" | "staging" | "prod" | "preview";

/**
 * Resolve dev/staging/prod/preview using signals available in both client and
 * server contexts. Order matters: explicit env vars win so SSR and client stay
 * in lockstep whenever they're set; the URL heuristic only fills in for
 * browsers that don't get those vars.
 *
 * `preview` is checked before the `prod` fallback: a PR preview is served from
 * a non-localhost host, so without this branch it would resolve to `prod` and
 * call the production API. See `runtime-env.ts` for how preview is detected.
 */
function getEnv(): Env {
  if (process.env.NODE_ENV === "development") return "dev";
  if (process.env.NEXT_PUBLIC_USE_STAGING_API === "true") return "staging";
  if (typeof window !== "undefined") {
    const { hostname } = window.location;
    if (
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname === "::1" ||
      hostname.includes("ngrok") ||
      hostname.endsWith(".local")
    ) {
      return "dev";
    }
    if (hostname.includes("staging")) return "staging";
  }
  if (isPreview()) return "preview";
  return "prod";
}

/**
 * Per-PR API origin for a preview, from the runtime env (`window.RUNTIME_ENV` /
 * `window.__ENV` with a `NEXT_PUBLIC_API_BASE_URL` SSR fallback). We deliberately do NOT fall back to
 * `siteConfig.apiUrl` here — a preview must never silently call the production
 * API. If the runtime env is missing, fail loudly so the misconfiguration is
 * obvious instead of leaking traffic to prod.
 */
function resolvePreviewAPIBaseURL(): string {
  const apiBaseURL = getRuntimeAPIBaseURL();
  if (!apiBaseURL) {
    throw new Error(
      "Preview context detected but no API_BASE_URL is configured in " +
        "window.RUNTIME_ENV / window.__ENV / NEXT_PUBLIC_API_BASE_URL. Refusing to fall back to the " +
        "production API. Check the preview's /runtime-env.js.",
    );
  }
  return apiBaseURL;
}

/**
 * The help center that /docs-sso signs staff into. Fixed per environment and
 * never read from the URL, so a sign-in pass can only ever be sent here.
 */
export function getDocsBaseURL(): string | null {
  const env = getEnv();
  if (env === "prod") return "https://docs.decodahealth.com";
  if (env === "dev") return "http://localhost:3100";
  return null;
}

export function getWindowBaseURL(tenantName?: string): string {
  if (typeof window === "undefined") {
    const local = getEnv() === "dev";
    const domain = local ? "localhost:3000" : "decodahealth.com";
    const protocol = local ? "http" : "https";
    return tenantName
      ? `${protocol}://${domain}/${tenantName}`
      : `${protocol}://${domain}`;
  } else {
    return tenantName
      ? `${window.location.origin}/${tenantName}`
      : window.location.origin;
  }
}

/** True when this local dev app is being viewed through an ngrok tunnel. */
export function isServedOverNgrok(): boolean {
  return (
    typeof window !== "undefined" &&
    window.location.hostname.includes("ngrok") &&
    getEnv() === "dev"
  );
}

/** The API's ngrok tunnel (`pnpm dev:ngrok`), used only when the app is served over ngrok. */
function ngrokAPIBaseURL(): string | undefined {
  const url = process.env.NEXT_PUBLIC_NGROK_API_URL;
  return url && isServedOverNgrok() ? url.replace(/\/$/, "") : undefined;
}

export function getAPIBaseURL(tenantName?: string): string {
  const env = getEnv();
  let baseURL: string;

  if (env === "preview") {
    baseURL = resolvePreviewAPIBaseURL();
  } else if (env === "staging") {
    baseURL = siteConfig.stagingApiUrl;
  } else if (env === "prod") {
    baseURL = siteConfig.apiUrl;
  } else if (ngrokAPIBaseURL()) {
    // A local app shared over ngrok reaches the API through its own tunnel. The
    // API takes the tenant from the TENANT header, so no subdomain here.
    return ngrokAPIBaseURL() as string;
  } else {
    const api_port = process.env.NEXT_PUBLIC_API_PORT || "8000";
    const hostname =
      typeof window !== "undefined" ? window.location.hostname : "localhost";
    baseURL = `http://${hostname}:${api_port}`;
  }
  // In preview the tenant rides the TENANT header + URL path; subdomain-
  // injecting it here would fabricate an unrouteable cross-origin host
  // (e.g. sandbox-pr-0.preview-0.<domain>) and break same-origin gating.
  if (tenantName && env !== "preview") {
    baseURL = baseURL.replace("//", `//${tenantName}.`);
  }
  return baseURL;
}

/**
 * Resolve the API base URL from a server context (no `window`). Used by server
 * components that fetch before first paint. Shares `getEnv` with
 * `getAPIBaseURL` so SSR and client resolve to the same backend.
 */
export function getServerAPIBaseURL(): string {
  const env = getEnv();
  if (env === "preview") {
    // SSR runs behind the gated public edge with no oauth2 cookie, so prefer a
    // loopback override (http://127.0.0.1:<apiPort>, injected server-side via
    // NEXT_PUBLIC_API_BASE_URL) to bypass the gate. Falls back to the runtime
    // gated host if unset.
    return process.env.NEXT_PUBLIC_API_BASE_URL || resolvePreviewAPIBaseURL();
  }
  if (env === "staging") return siteConfig.stagingApiUrl;
  if (env === "prod") return siteConfig.apiUrl;
  const port =
    process.env.API_PORT ?? process.env.NEXT_PUBLIC_API_PORT ?? "8000";
  return `http://localhost:${port}`;
}
